import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Table, Tag, Space, Tooltip, Pagination, Popover, Avatar } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteUnitApi from "../../api/DeleteUnitApi";
import RemoveUnitHeadApi from "../../api/units/RemoveUnitHeadApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import AddEditUnitHeadModal from "../modal/add/AddEditUnitHeadModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditUnitNameModal from "../modal/edit/EditUnitNameModal";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function OrganizationTable(props) {
  const [editModal, setEditModal] = useState(false);
  const [editUnit, setEditUnit] = useState();
  const [deleteModal, setDeleteModal] = useState("");
  const [deleteUnitId, setDeleteUnitId] = useState("");
  const [removeUnitHeadModal, setRemoveUnitHeadModal] = useState(false);
  const [removeUnitHeadId, setRemoveUnitHeadId] = useState("");
  const [addEditHeadModal, setAddEditHeadModal] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const setupAccountState = useSelector(
    (state) => state.setupAccountState.value
  );
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  let navigate = useNavigate();
  const pageLimit = 40;

  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  /*If page changed from parent component */
  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function deleteUnit() {
    const apiResponse = await DeleteUnitApi(deleteUnitId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Unit deleted");
      if (props.data?.units?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeleteUnitId("");
  }

  async function removeUnitHead() {
    const apiResponse = await RemoveUnitHeadApi(removeUnitHeadId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message) {
      openNotificationSuccess("bottomRight", "Unit head removed");
      if (props.data?.units?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't remove. Try again later.");
    }
    setRemoveUnitHeadId("");
  }

  const columns = [
    {
      title: "Unit name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="flex items-center gap-1">
          {record.name}{" "}
          {permissionAbility(userLevel, "organization", "edit") && (
            <Tooltip title={"Edit name"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setEditModal(true);
                  setEditUnit(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer text-xs"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Level",
      key: "level",
      dataIndex: "level",
      sorter: (a, b) => a.organizationLevel?.order - b.organizationLevel?.order,
      render: (text, record) => (
        <Tag color={record.organizationLevel?.color}>
          {record.organizationLevel?.name.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Head unit",
      dataIndex: "headUnit",
      key: "headUnit",
      render: (text, record) => (
        <>
          {record.headUnit?.name ? (
            <div className="flex items-center">
              <div
                className="w-2 h-2 mr-1 rounded-full"
                style={{ backgroundColor: record.headUnit?.level?.color }}
              />
              <div>{record.headUnit?.name}</div>
              <Popover
                placement="top"
                content={
                  <div className="text-xs">
                    {record.name + " < " + record.headUnit?.name}
                    {record.headUnit?.headUnit &&
                      " < " + record.headUnit?.headUnit?.name}
                  </div>
                }
                trigger="hover"
              >
                <div className="text-gray-500 text-xs ml-1">
                  <InfoCircleOutlined />
                </div>
              </Popover>
            </div>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: "Sub units",
      dataIndex: "subUnitCount",
      key: "subUnit",
      render: (text, record) => <div>{record.subUnitsCount || "0"}</div>,
    },
    {
      title: "Positions",
      dataIndex: "positionsCount",
      key: "positionsCount",
      render: (text, record) => <div>{record.positionsCount || "0"}</div>,
    },
    {
      title: "Unit head",
      dataIndex: "unitHead",
      key: "unitHead",
      render: (text, record) =>
        record.unitHead ? (
          <div className="flex items-start">
            <div className="flex items-center gap-1 mr-1">
              <div>
                {record.unitHead?.user?.imageUrl ? (
                  <Avatar src={record.unitHead?.user?.imageUrl} />
                ) : (
                  <Avatar>
                    {record.unitHead?.user?.firstName ? (
                      record.unitHead?.user?.firstName?.charAt(0).toUpperCase()
                    ) : (
                      <UserOutlined />
                    )}
                  </Avatar>
                )}
              </div>
              <div>
                <div>
                  {record.unitHead?.user?.firstName
                    ? record.unitHead?.user?.firstName +
                      " " +
                      record.unitHead?.user?.lastName
                    : "-"}
                </div>
                <div className="text-xs">
                  {record.unitHead?.designation?.abbreviation}
                </div>
              </div>
            </div>

            {permissionAbility(userLevel, "organization", "delete") && (
              <Tooltip title={"Remove unit head"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveUnitHeadModal(true);
                    setRemoveUnitHeadId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer mt-1 text-xs"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            )}
          </div>
        ) : (
          permissionAbility(userLevel, "organization", "add") && (
            <Tooltip title={"Add unit head"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setAddEditHeadModal(true);
                  setEditUnit(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer inline"
              >
                <PlusCircleOutlined />
              </div>
            </Tooltip>
          )
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "organization", "delete") &&
            (record.canDelete ? (
              <Tooltip title={"Delete"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteModal("delete");
                    setDeleteUnitId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            ) : record.canArchive ? (
              <Tooltip title={"Archive"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteModal("archive");
                    setDeleteUnitId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <FolderAddOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={getDeleteMessage(record.organizationLevel?.order || 2)}
                trigger="hover"
              >
                <div className="text-gray-500">
                  <FolderAddOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  function getDeleteMessage(order) {
    let output = "";

    if (order === 1) {
      output = (
        <div className="text-xs w-48">
          Can't delete this Unit. This is the top unit.
        </div>
      );
    } else {
      output = (
        <div className="text-xs w-48">
          Can't delete this Unit. This unit have active sub units or positions.
        </div>
      );
    }

    return output;
  }
  return (
    <>
      <Table
        columns={columns}
        dataSource={props.data?.units}
        pagination={false}
        loading={loading}
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(
                setupAccountState
                  ? `/settings/unit/${record.id}`
                  : `/setup-account/unit/${record.id}`
              );
            },
          };
        }}
        rowClassName={"cursor-pointer hover:shadow"}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => props.changePage(num)}
          current={page}
          total={props.data?.totalCount || 0}
          pageSize={pageLimit}
        />
      </div>
      <EditUnitNameModal
        visible={editModal}
        handleCancel={() => {
          setEditModal(false);
          setEditUnit();
        }}
        unit={editUnit}
        handleOk={() => props.changePage(page)}
      />
      <AddEditUnitHeadModal
        visible={addEditHeadModal}
        handleCancel={() => {
          setAddEditHeadModal(false);
          setEditUnit();
        }}
        unit={editUnit}
        handleOk={() => props.changePage(page)}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal("");
          setDeleteUnitId("");
        }}
        handleOk={() => deleteUnit()}
        text={`Are you sure want to ${deleteModal} this Unit?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
      <ConfirmationModal
        visible={removeUnitHeadModal}
        handleCancel={() => {
          setRemoveUnitHeadModal(false);
          setRemoveUnitHeadId("");
        }}
        handleOk={() => removeUnitHead()}
        text="Are you sure want to remove this unit head?"
        title="Remove unit head"
      />
    </>
  );
}

export default OrganizationTable;
